// src/components/payment/PaymentModal.tsx
// Full-featured payment modal. Manages:
//   – Plan selection with PlanCard sub-components
//   – Promo-code input (PromoCodeInput sub-component)
//   – BOG payment initiation → redirects browser to BOG checkout
//   – 100%-off promo flow → shows in-modal success without BOG redirect
//   – Loading / error / cancel / success views (state machine)
//   – Focus trap, body scroll lock, Escape-to-close, backdrop click to close
//
// The /payment-success and /payment-cancel pages (Session 7) handle the return
// from BOG. This modal's success view is only shown for free-promo activation.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePaymentStore, type PlanId } from '../../stores/usePaymentStore';
import { useAuthStore } from '../../stores/useAuthStore';
import {
  initiatePlanPayment,
  invalidateSubscriptionCache,
  refreshSubscriptionInStore,
  PLANS,
  PLAN_NAMES,
} from '../../services/paymentService';
import { PlanCard } from './PlanCard.tsx';
import { PromoCodeInput } from './PromoCodeInput.tsx';

// ── Google Fonts injection (one-time side-effect) ─────────────────────────────
// Injects DM Sans + Syne if not already present. Respects no-CSS-files rule.
function ensureGoogleFonts(): void {
  const id = 'esp-payment-fonts';
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id   = id;
  link.rel  = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Syne:wght@700;800&display=swap';
  document.head.appendChild(link);
}

// ── Sub-views ────────────────────────────────────────────────────────────────

interface SpinnerProps { color?: string }
function Spinner({ color = '#6c5ce7' }: SpinnerProps): React.JSX.Element {
  return (
    <div
      style={{
        width: '48px',
        height: '48px',
        border: `3px solid rgba(108,92,231,0.15)`,
        borderTopColor: color,
        borderRadius: '50%',
        animation: 'esp-pm-spin 0.75s linear infinite',
        marginBottom: '20px',
        flexShrink: 0,
      }}
    />
  );
}

interface StateViewProps {
  iconEl: React.ReactNode;
  title: string;
  message: string;
  detail?: React.ReactNode;
  actions?: React.ReactNode;
}
function StateView({ iconEl, title, message, detail, actions }: StateViewProps): React.JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '340px',
        padding: '48px 36px',
        textAlign: 'center',
      }}
    >
      <div style={{ marginBottom: '24px' }}>{iconEl}</div>
      <div
        style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: '1.75rem',
          fontWeight: 800,
          color: '#fff',
          margin: '0 0 10px',
        }}
      >
        {title}
      </div>
      <p
        style={{
          color: 'rgba(255,255,255,0.5)',
          fontSize: '0.94rem',
          lineHeight: 1.6,
          margin: '0 0 24px',
          maxWidth: '380px',
        }}
      >
        {message}
      </p>
      {detail}
      {actions}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function PaymentModal(): React.JSX.Element | null {
  const {
    isOpen,
    view,
    selectedPlan,
    successPlan,
    successExpiry,
    errorTitle,
    errorMessage,
    closeModal,
    setView,
    setSelectedPlan,
    setError,
    resetModal,
  } = usePaymentStore();

  const user         = useAuthStore((s) => s.user);
  const openAuthModal = useAuthStore((s) => s.openAuthModal);

  // Animation state — trails isOpen so the exit transition completes
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible,    setIsVisible]    = useState(false);

  // Promo state (lives here so plan-change can reset it)
  const [appliedPromo, setAppliedPromo] = useState<{
    code: string;
    finalPrice: number;
  } | null>(null);

  // Grid columns responsive
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);

  const overlayRef = useRef<HTMLDivElement>(null);

  // ── Fonts + responsive listener ───────────────────────────────────────────

  useEffect(() => {
    ensureGoogleFonts();

    // Inject keyframe for spinner (one-time)
    const kfId = 'esp-pm-keyframes';
    if (!document.getElementById(kfId)) {
      const style = document.createElement('style');
      style.id = kfId;
      style.textContent = '@keyframes esp-pm-spin { to { transform: rotate(360deg); } }';
      document.head.appendChild(style);
    }

    const onResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Open / close animation ─────────────────────────────────────────────────

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      document.body.style.overflow = 'hidden';
      // Double rAF ensures the initial opacity-0 frame is painted before transition starts
      requestAnimationFrame(() => requestAnimationFrame(() => setIsVisible(true)));
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
        document.body.style.overflow = '';
        resetModal();
        setAppliedPromo(null);
      }, 400);
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // ── Escape key ────────────────────────────────────────────────────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    closeModal();
    // Re-verify subscription so a premium user who just browsed the modal
    // doesn't get erroneously locked out. Fire-and-forget.
    refreshSubscriptionInStore().catch(() => {});
  }, [closeModal]);

  const handlePlanSelect = (planId: PlanId) => {
    setSelectedPlan(planId);
    setAppliedPromo(null); // reset promo whenever plan changes
  };

  const handlePromoApplied = (code: string, finalPrice: number) => {
    setAppliedPromo({ code, finalPrice });
  };

  const handleFreePromoActivated = async () => {
    // Subscription was activated server-side by validate-promo Netlify function.
    invalidateSubscriptionCache();
    await refreshSubscriptionInStore();
    setView('success');
    // Auto-close after brief success display
    setTimeout(() => handleClose(), 2400);
  };

  const handlePayNow = async () => {
    if (!selectedPlan) return;

    // Guard: must be authenticated
    if (!user || user.isAnonymous) {
      handleClose();
      openAuthModal('login');
      return;
    }

    setView('loading');

    try {
      // initiatePlanPayment redirects the browser to BOG — throws on error.
      // If we somehow return from it (shouldn't happen), fall through to success.
      await initiatePlanPayment(selectedPlan, appliedPromo?.code ?? undefined);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again or contact support.';
      setError('Payment Failed', msg);
      setView('error');
    }
  };

  // ── Derived CTA label ─────────────────────────────────────────────────────

  const getCtaLabel = (): string => {
    if (!selectedPlan) return 'Select a Plan';
    const plan = PLANS.find((p) => p.id === selectedPlan);
    if (!plan) return 'Pay Now';
    const price = appliedPromo ? appliedPromo.finalPrice : plan.price;
    return `Pay $${price.toFixed(2)} — ${PLAN_NAMES[selectedPlan]}`;
  };

  const getCtaColor = (): string => {
    if (!selectedPlan) return 'linear-gradient(135deg, #4a4a6a, #3a3a5a)';
    const plan = PLANS.find((p) => p.id === selectedPlan);
    return `linear-gradient(135deg, ${plan?.color ?? '#6c5ce7'}, ${plan?.color ?? '#4a90e2'}bb)`;
  };

  // ── Don't render at all until shouldRender is true ────────────────────────

  if (!shouldRender) return null;

  // ── Styles ────────────────────────────────────────────────────────────────

  const styles: Record<string, React.CSSProperties> = {
    // ── Overlay ────────────────────────────────────────────────────────────
    overlay: {
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '12px' : '16px',
      background: 'rgba(4, 4, 14, 0.84)',
      backdropFilter: 'blur(14px)',
      WebkitBackdropFilter: 'blur(14px)',
      opacity: isVisible ? 1 : 0,
      transition: 'opacity 0.35s ease',
    },

    // ── Modal shell ────────────────────────────────────────────────────────
    modal: {
      position: 'relative',
      width: '100%',
      maxWidth: '980px',
      maxHeight: '92vh',
      overflowY: 'auto',
      borderRadius: isMobile ? '18px' : '24px',
      background: '#0a0a18',
      border: '1px solid rgba(255,255,255,0.07)',
      boxShadow:
        '0 40px 120px rgba(0,0,0,0.75), 0 0 0 1px rgba(108,92,231,0.12)',
      transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.97)',
      transition: 'transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
      fontFamily: "'DM Sans', system-ui, sans-serif",
    },

    // Top decorative glow line (pseudo-element via a real element here)
    topGlow: {
      position: 'absolute',
      top: 0,
      left: '10%',
      right: '10%',
      height: '1px',
      background:
        'linear-gradient(90deg, transparent, #6c5ce7 30%, #4a90e2 70%, transparent)',
      borderRadius: '0 0 4px 4px',
      pointerEvents: 'none',
    },

    // ── Close button ───────────────────────────────────────────────────────
    closeBtn: {
      position: 'absolute',
      top: '16px',
      right: '16px',
      width: '34px',
      height: '34px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '50%',
      color: 'rgba(255,255,255,0.5)',
      fontSize: '18px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      lineHeight: 1,
      zIndex: 2,
      fontFamily: 'inherit',
    },

    // ── Header ─────────────────────────────────────────────────────────────
    header: {
      padding: isMobile ? '28px 20px 18px' : '36px 36px 22px',
      textAlign: 'center',
      position: 'relative',
    },

    eyebrow: {
      display: 'inline-block',
      fontSize: '0.68rem',
      fontWeight: 700,
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      color: '#a29bfe',
      background: 'rgba(108,92,231,0.1)',
      padding: '4px 14px',
      borderRadius: '20px',
      marginBottom: '14px',
    },

    title: {
      fontFamily: "'Syne', sans-serif",
      fontSize: isMobile ? '1.6rem' : '2.1rem',
      fontWeight: 800,
      color: '#fff',
      lineHeight: 1.15,
      margin: '0 0 8px',
    },

    titleAccent: {
      background: 'linear-gradient(135deg, #a29bfe, #74b9ff)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },

    subtitle: {
      color: 'rgba(255,255,255,0.42)',
      fontSize: '0.92rem',
      margin: 0,
    },

    // ── Plans grid ─────────────────────────────────────────────────────────
    plansGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
      gap: isMobile ? '10px' : '16px',
      padding: isMobile ? '0 14px 18px' : '0 28px 24px',
    },

    // ── Footer / CTA ───────────────────────────────────────────────────────
    footer: {
      padding: isMobile ? '0 14px 22px' : '0 28px 28px',
    },

    cta: {
      width: '100%',
      padding: '15px 20px',
      border: 'none',
      borderRadius: '14px',
      fontFamily: "'DM Sans', inherit",
      fontSize: '1rem',
      fontWeight: 700,
      cursor: selectedPlan ? 'pointer' : 'not-allowed',
      background: getCtaColor(),
      color: '#fff',
      letterSpacing: '0.02em',
      transition: 'transform 0.22s ease, box-shadow 0.22s ease, opacity 0.2s ease',
      boxShadow: selectedPlan ? '0 8px 24px rgba(108,92,231,0.3)' : 'none',
      opacity: selectedPlan ? 1 : 0.45,
    },

    ctaNote: {
      textAlign: 'center',
      marginTop: '12px',
      fontSize: '0.76rem',
      color: 'rgba(255,255,255,0.25)',
      lineHeight: 1.5,
    },

    ctaLink: {
      color: 'rgba(255,255,255,0.38)',
    },

    // ── Trust bar ──────────────────────────────────────────────────────────
    trustBar: {
      display: isMobile ? 'none' : 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '24px',
      padding: '14px 28px 22px',
      borderTop: '1px solid rgba(255,255,255,0.04)',
      flexWrap: 'wrap',
    },

    trustItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '7px',
      fontSize: '0.76rem',
      color: 'rgba(255,255,255,0.25)',
    },

    trustIcon: {
      fontSize: '0.82rem',
      color: 'rgba(255,255,255,0.18)',
    },

    // ── Success detail box ─────────────────────────────────────────────────
    successDetail: {
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: '12px',
      padding: '16px 24px',
      marginBottom: '24px',
      width: '100%',
      maxWidth: '340px',
      fontSize: '0.87rem',
      color: 'rgba(255,255,255,0.55)',
      textAlign: 'left',
      lineHeight: 1.7,
    },

    successActive: {
      color: '#00b894',
      fontWeight: 700,
    },

    // ── Button rows ────────────────────────────────────────────────────────
    btnRow: {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap',
      justifyContent: 'center',
    },

    btnPrimary: {
      padding: '13px 28px',
      borderRadius: '10px',
      fontFamily: 'inherit',
      fontSize: '0.92rem',
      fontWeight: 700,
      cursor: 'pointer',
      border: 'none',
      background: 'linear-gradient(135deg, #6c5ce7, #4a90e2)',
      color: '#fff',
      boxShadow: '0 6px 18px rgba(108,92,231,0.3)',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    },

    btnGhost: {
      padding: '13px 28px',
      borderRadius: '10px',
      fontFamily: 'inherit',
      fontSize: '0.92rem',
      fontWeight: 700,
      cursor: 'pointer',
      border: '1px solid rgba(255,255,255,0.1)',
      background: 'rgba(255,255,255,0.05)',
      color: 'rgba(255,255,255,0.55)',
      transition: 'background 0.2s ease, color 0.2s ease',
    },

    // Loading view
    loadingWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '320px',
      padding: '48px 36px',
      textAlign: 'center',
    },

    loadingTitle: {
      fontFamily: "'Syne', sans-serif",
      fontSize: '1.6rem',
      fontWeight: 800,
      color: '#fff',
      margin: '0 0 10px',
    },

    loadingMsg: {
      color: 'rgba(255,255,255,0.4)',
      fontSize: '0.9rem',
    },
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      ref={overlayRef}
      style={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="Get Premium Access"
      onClick={(e) => {
        if (e.target === overlayRef.current) handleClose();
      }}
    >
      <div style={styles.modal}>
        {/* Top glow line */}
        <div style={styles.topGlow} aria-hidden="true" />

        {/* Close button (always visible) */}
        <CloseButton style={styles.closeBtn} onClick={handleClose} />

        {/* ── Main view ────────────────────────────────────────────────── */}
        {view === 'main' && (
          <>
            {/* Header */}
            <div style={styles.header}>
              <div style={styles.eyebrow}>Unlock Everything</div>
              <h2 style={styles.title}>
                Get{' '}
                <span style={styles.titleAccent}>Premium Access</span>
              </h2>
              <p style={styles.subtitle}>
                All units · Full page access · Audio & video lessons · Progress sync
              </p>
            </div>

            {/* Plan cards */}
            <div style={styles.plansGrid} role="radiogroup" aria-label="Choose a plan">
              {PLANS.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isSelected={selectedPlan === plan.id}
                  onSelect={() => handlePlanSelect(plan.id)}
                />
              ))}
            </div>

            {/* CTA button */}
            <div style={styles.footer}>
              <CtaButton
                label={getCtaLabel()}
                disabled={!selectedPlan}
                style={styles.cta}
                onClick={handlePayNow}
              />
              <p style={styles.ctaNote}>
                Secure checkout via Bank of Georgia &nbsp;·&nbsp;
                Questions?{' '}
                <a href="mailto:support@espforsports.com" style={styles.ctaLink}>
                  support@espforsports.com
                </a>
              </p>
            </div>

            {/* Promo code (visible only after plan selection) */}
            {selectedPlan && (
              <PromoCodeInput
                selectedPlan={selectedPlan}
                onPromoApplied={handlePromoApplied}
                onFreePromoActivated={handleFreePromoActivated}
                onPromoReset={() => setAppliedPromo(null)}
              />
            )}

            {/* Trust bar */}
            <div style={styles.trustBar}>
              {[
                ['Encrypted Payment'],
                ['Secure Checkout'],
                ['Instant Access'],
                ['Email Support'],
              ].map(([label]) => (
                <div key={label} style={styles.trustItem}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'rgba(255,255,255,0.18)', display: 'inline-block', flexShrink: 0 }} />
                  {label}
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Loading view ──────────────────────────────────────────────── */}
        {view === 'loading' && (
          <div style={styles.loadingWrapper}>
            <Spinner />
            <div style={styles.loadingTitle}>
              Preparing{' '}
              {selectedPlan ? PLAN_NAMES[selectedPlan] : 'Checkout'}…
            </div>
            <p style={styles.loadingMsg}>
              Connecting to Bank of Georgia. You will be redirected shortly.
            </p>
          </div>
        )}

        {/* ── Success view (100% promo / free activation) ───────────────── */}
        {view === 'success' && (
          <StateView
            iconEl={
              <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 32px rgba(52,211,153,0.15)' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
            }
            title="You're All Set!"
            message="Your subscription is now active. All premium content is unlocked."
            detail={
              <div style={styles.successDetail}>
                <div>
                  <strong style={{ color: '#fff' }}>Status:</strong>{' '}
                  <span style={styles.successActive}>Active ✓</span>
                </div>
                {successPlan && (
                  <div>
                    <strong style={{ color: '#fff' }}>Plan:</strong>{' '}
                    {PLAN_NAMES[successPlan]}
                  </div>
                )}
                {successExpiry && (
                  <div>
                    <strong style={{ color: '#fff' }}>Valid Until:</strong>{' '}
                    {successExpiry}
                  </div>
                )}
              </div>
            }
            actions={
              <div style={styles.btnRow}>
                <button style={styles.btnPrimary} onClick={handleClose}>
                  Start Learning →
                </button>
              </div>
            }
          />
        )}

        {/* ── Cancel view ───────────────────────────────────────────────── */}
        {view === 'cancel' && (
          <StateView
            iconEl={
              <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
              </div>
            }
            title="Payment Cancelled"
            message="No charge was made. You can try again anytime — your free access to Unit 1 is still available."
            actions={
              <div style={styles.btnRow}>
                <button style={styles.btnPrimary} onClick={() => setView('main')}>
                  Try Again
                </button>
                <button style={styles.btnGhost} onClick={handleClose}>
                  Maybe Later
                </button>
              </div>
            }
          />
        )}

        {/* ── Error view ────────────────────────────────────────────────── */}
        {view === 'error' && (
          <StateView
            iconEl={
              <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </div>
            }
            title={errorTitle}
            message={errorMessage}
            actions={
              <div style={styles.btnRow}>
                <button style={styles.btnPrimary} onClick={() => setView('main')}>
                  Try Again
                </button>
                <button style={styles.btnGhost} onClick={handleClose}>
                  Close
                </button>
              </div>
            }
          />
        )}
      </div>
    </div>
  );
}

// ── Small helper components ───────────────────────────────────────────────────

interface CloseButtonProps {
  style: React.CSSProperties;
  onClick: () => void;
}
function CloseButton({ style, onClick }: CloseButtonProps): React.JSX.Element {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      aria-label="Close"
      onClick={onClick}
      style={{
        ...style,
        background: hovered ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)',
        color: hovered ? '#fff' : 'rgba(255,255,255,0.5)',
        transform: hovered ? 'rotate(90deg)' : 'rotate(0deg)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      ×
    </button>
  );
}

interface CtaButtonProps {
  label: string;
  disabled: boolean;
  style: React.CSSProperties;
  onClick: () => void;
}
function CtaButton({ label, disabled, style, onClick }: CtaButtonProps): React.JSX.Element {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        ...style,
        transform: hovered && !disabled ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow:
          hovered && !disabled
            ? '0 14px 32px rgba(108,92,231,0.45)'
            : style.boxShadow as string,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {label}
    </button>
  );
}